import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { AppBar, Box, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import SidebarIcon from '@/components/icons/SidebarIcon'
import EnvironmentRibbon from '@/components/layouts/EnvironmentRibbon'
import NavBarUserMenu from '@/components/navigation/NavBarUserMenu'
import { TOP_BAR_HEIGHT } from '@/constants/layout'
import { useSidebar } from '@/contexts/SidebarContext'
import environment from '@/environment'
import { useWhitelabelConfigurationContext } from '@/features/whitelabel/context/WhitelabelContext'

function ShortcutKey({ children }: Readonly<{ children: string }>) {
  return (
    <Box
      component="kbd"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        px: 0.5,
        ml: 0.25,
        fontFamily: 'inherit',
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1,
        color: alpha(theme.palette.common.white, 0.92),
        borderRadius: 0.75,
        backgroundColor: alpha(theme.palette.common.white, 0.16),
      })}
    >
      {children}
    </Box>
  )
}

function AppTopBar() {
  const { t } = useTranslation()
  const { onToggle, isCollapsed } = useSidebar()
  const { branding, mailto } = useWhitelabelConfigurationContext()

  const toggleLabel = isCollapsed ? t('component.navbar.show_sidebar') : t('component.navbar.hide_sidebar')
  const toggleTooltip = (
    <Stack alignItems="center" direction="row" gap={0.5}>
      <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
        {toggleLabel}
      </Typography>
      <ShortcutKey>[</ShortcutKey>
      <ShortcutKey>]</ShortcutKey>
    </Stack>
  )

  return (
    <AppBar
      elevation={0}
      position="static"
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0,
      })}
    >
      <Toolbar
        disableGutters
        sx={(theme) => ({
          minHeight: TOP_BAR_HEIGHT,
          height: TOP_BAR_HEIGHT,
          px: 2,
          gap: 1.5,
          [theme.breakpoints.up('sm')]: {
            minHeight: TOP_BAR_HEIGHT,
            height: TOP_BAR_HEIGHT,
          },
        })}
        variant="dense"
      >
        <Stack alignItems="center" direction="row" gap={1.5} sx={{ flexShrink: 0 }}>
          <Tooltip arrow enterDelay={400} enterNextDelay={400} placement="bottom" title={toggleTooltip}>
            <IconButton
              aria-label={toggleLabel}
              aria-pressed={isCollapsed}
              size="small"
              sx={(theme) => ({
                color: 'text.secondary',
                p: 0.5,
                borderRadius: 1,
                transition: `color 160ms cubic-bezier(0.16, 1, 0.3, 1), background-color 160ms cubic-bezier(0.16, 1, 0.3, 1)`,
                '& svg': { fontSize: 20 },
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
                '&:focus-visible': {
                  outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                  outlineOffset: 2,
                },
              })}
              onClick={onToggle}
            >
              <SidebarIcon />
            </IconButton>
          </Tooltip>
          <Link aria-label={branding.name} style={{ display: 'inline-flex', alignItems: 'center' }} to="/">
            <Box
              alt={branding.name}
              component="img"
              src={branding.coloredLogo}
              sx={{
                height: 18,
                maxWidth: 128,
                display: 'block',
                transition: 'opacity 160ms cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': { opacity: 0.78 },
              }}
            />
          </Link>
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Stack alignItems="center" direction="row" gap={1} sx={{ flexShrink: 0 }}>
          {environment.environmentName !== 'production' && <EnvironmentRibbon name={environment.environmentName} />}
          {mailto?.support && (
            <Tooltip arrow placement="bottom" title={t('user_profile.support.title')}>
              <IconButton
                aria-label={t('user_profile.support.title')}
                component="a"
                href={`mailto:${mailto.support}`}
                size="small"
                sx={(theme) => ({
                  color: 'text.secondary',
                  p: 0.5,
                  borderRadius: 1,
                  transition: `color 160ms cubic-bezier(0.16, 1, 0.3, 1), background-color 160ms cubic-bezier(0.16, 1, 0.3, 1)`,
                  '& svg': { fontSize: 20 },
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    outlineOffset: 2,
                  },
                })}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
          <NavBarUserMenu />
        </Stack>
      </Toolbar>
    </AppBar>
  )
}

export default AppTopBar
